import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import '../styles/TeamDetails.css';

const TeamDetails = ({ stakingContract, userInfo, refreshData }) => {
  const [loading, setLoading] = useState(false);
  const [teamData, setTeamData] = useState({
    levelBreakdown: [],
    totalTeamVolume: '0',
    totalLevelIncome: '0',
    totalTeamMembers: 0
  });
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [activities, setActivities] = useState([]);
  const [levelMembers, setLevelMembers] = useState({});
  const [membersPagination, setMembersPagination] = useState({});
  const [loadingMembers, setLoadingMembers] = useState({});
  const [error, setError] = useState(null);

  // Commission rates from your contract
  const commissionRates = useMemo(() => ({
    1: '6%',    // Direct referral
    2: '1%',    // 100 basis points
    3: '3%',    // 300 basis points  
    4: '4%',    // 400 basis points
    5: '6%',    // 600 basis points
    6: '1%',    // 100 basis points
    7: '0.75%', 8: '0.75%', 9: '0.75%', 10: '0.75%', // 75 basis points
    11: '0.5%', 12: '0.5%', 13: '0.5%', 14: '0.5%', 15: '0.5%', // 50 basis points
    16: '0.125%', 17: '0.125%', 18: '0.125%', 19: '0.125%', 20: '0.125%' // 12 basis points
  }), []);

  // Check if contract has the new getTeamBreakdown function
  const hasTeamBreakdown = useMemo(() => {
    return stakingContract && typeof stakingContract.getTeamBreakdown === 'function';
  }, [stakingContract]);

  // Main data loading function using the new getTeamBreakdown
  const loadTeamData = useCallback(async () => {
    if (!stakingContract || !userInfo?.id) {
      setError('Missing contract or user info');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if user exists
      const userExists = await stakingContract.userIdExists(userInfo.id);
      if (!userExists) {
        setError('User not found in contract');
        resetTeamData();
        return;
      }

      // Load data in parallel
      const [teamBreakdownResult, incomeBreakdown, userActivities] = await Promise.all([
        // Use the new getTeamBreakdown function for instant results
        hasTeamBreakdown 
          ? stakingContract.getTeamBreakdown(userInfo.id)
          : null,
        // Get income data
        stakingContract.getUserIncomeBreakdown(userInfo.id).catch(() => [0, 0, 0, 0]),
        // Get recent activities
        stakingContract.getUserActivities(userInfo.id, 20).catch(() => [])
      ]);

      // Process income data
      const totalLevelIncome = ethers.utils.formatEther(incomeBreakdown[2] || 0);

      // Process activities - filter for level rewards only
      const levelRewards = (userActivities || [])
        .filter(activity => activity[4] === 4) // activityType 4 = level rewards
        .map(activity => ({
          timestamp: activity[0].toString(),
          amount: ethers.utils.formatEther(activity[2]),
          level: activity[3].toString(),
          fromUserId: activity[5].toString()
        }))
        .sort((a, b) => b.timestamp - a.timestamp);

      setActivities(levelRewards);

      // Process team breakdown data
      if (hasTeamBreakdown && teamBreakdownResult) {
        const [levelCounts, levelVolumes, totalMembers, totalVolume] = teamBreakdownResult;
        
        console.log('Team breakdown data:', {
          levelCounts: levelCounts.map(c => c.toString()),
          levelVolumes: levelVolumes.map(v => ethers.utils.formatEther(v)),
          totalMembers: totalMembers.toString(),
          totalVolume: ethers.utils.formatEther(totalVolume)
        });

        // Build level breakdown with real data from contract
        const levelBreakdown = [];
        for (let i = 0; i < 20; i++) {
          const level = i + 1;
          const count = parseInt(levelCounts[i].toString());
          const volume = ethers.utils.formatEther(levelVolumes[i]);
          
          levelBreakdown.push({
            level,
            count,
            volume,
            commission: commissionRates[level] || '0%'
          });
        }

        setTeamData({
          levelBreakdown,
          totalTeamVolume: ethers.utils.formatEther(totalVolume),
          totalLevelIncome,
          totalTeamMembers: parseInt(totalMembers.toString())
        });

      } else {
        // Fallback for contracts without getTeamBreakdown
        console.warn('getTeamBreakdown function not available, using fallback');
        await loadTeamDataFallback(totalLevelIncome);
      }

    } catch (error) {
      console.error('Failed to load team data:', error);
      setError(`Failed to load team data: ${error.message}`);
      resetTeamData();
    } finally {
      setLoading(false);
    }
  }, [stakingContract, userInfo, hasTeamBreakdown, commissionRates]);

  // Fallback method for older contracts
  const loadTeamDataFallback = useCallback(async (totalLevelIncome) => {
    try {
      // Get level counts using existing functions
      const levelPromises = Array.from({ length: 20 }, (_, i) => {
        const level = i + 1;
        return stakingContract.getUserLevelCount(userInfo.id, level)
          .then(count => ({ level, count: parseInt(count.toString()) }))
          .catch(() => ({ level, count: 0 }));
      });

      const levelResults = await Promise.all(levelPromises);
      const totalMembers = levelResults.reduce((sum, { count }) => sum + count, 0);

      const levelBreakdown = levelResults.map(({ level, count }) => ({
        level,
        count,
        volume: level === 1 ? (userInfo.teamVolume || '0') : '0', // Only show level 1 volume
        commission: commissionRates[level] || '0%'
      }));

      setTeamData({
        levelBreakdown,
        totalTeamVolume: userInfo.teamVolume || '0',
        totalLevelIncome,
        totalTeamMembers: totalMembers
      });

    } catch (error) {
      console.error('Fallback team data loading failed:', error);
      // Emergency fallback
      const directReferrals = parseInt(userInfo.directReferrals || 0);
      const levelBreakdown = Array.from({ length: 20 }, (_, i) => ({
        level: i + 1,
        count: i === 0 ? directReferrals : 0,
        volume: i === 0 ? (userInfo.teamVolume || '0') : '0',
        commission: commissionRates[i + 1] || '0%'
      }));

      setTeamData({
        levelBreakdown,
        totalTeamVolume: userInfo.teamVolume || '0',
        totalLevelIncome,
        totalTeamMembers: directReferrals
      });
    }
  }, [stakingContract, userInfo, commissionRates]);

  // Load level members with pagination
  const loadLevelMembers = useCallback(async (level, page = 1, pageSize = 10) => {
    if (!stakingContract || !userInfo?.id) return;
    
    const levelKey = `level_${level}`;
    setLoadingMembers(prev => ({ ...prev, [levelKey]: true }));
    
    try {
      const offset = (page - 1) * pageSize;
      const result = await stakingContract.getLevelMembers(userInfo.id, level, offset, pageSize);
      
      const memberIds = result[0] || [];
      const totalCount = parseInt((result[1] || 0).toString());
      
      // Get member details
      const memberDetails = await Promise.allSettled(
        memberIds.map(async (memberId) => {
          const memberInfo = await stakingContract.getUserInfo(memberId);
          return {
            id: memberId.toString(),
            wallet: memberInfo[0],
            totalStaked: ethers.utils.formatEther(memberInfo[4]),
            teamVolume: ethers.utils.formatEther(memberInfo[6]),
            joinDate: new Date() // Would need registration timestamp for real date
          };
        })
      );

      const successfulMembers = memberDetails
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value);
      
      setLevelMembers(prev => ({
        ...prev,
        [levelKey]: {
          members: successfulMembers,
          total: totalCount,
          page,
          pageSize
        }
      }));
      
      setMembersPagination(prev => ({
        ...prev,
        [levelKey]: { 
          page, 
          total: Math.ceil(totalCount / pageSize),
          totalMembers: totalCount
        }
      }));
      
    } catch (error) {
      console.error(`Failed to load level ${level} members:`, error);
      setLevelMembers(prev => ({
        ...prev,
        [levelKey]: {
          members: [],
          total: 0,
          page,
          pageSize,
          error: error.message
        }
      }));
    } finally {
      setLoadingMembers(prev => ({ ...prev, [levelKey]: false }));
    }
  }, [stakingContract, userInfo?.id]);

  // Handle level expand/collapse
  const handleLevelExpand = useCallback(async (level) => {
    const levelCount = teamData.levelBreakdown.find(l => l.level === level)?.count || 0;
    
    if (expandedLevel === level) {
      setExpandedLevel(null);
    } else if (levelCount > 0) {
      setExpandedLevel(level);
      
      // Load members when expanding
      const levelKey = `level_${level}`;
      if (!levelMembers[levelKey]) {
        await loadLevelMembers(level, 1, 10);
      }
    }
  }, [expandedLevel, teamData.levelBreakdown, levelMembers, loadLevelMembers]);

  // Handle pagination
  const handlePageChange = useCallback(async (level, newPage) => {
    await loadLevelMembers(level, newPage, 10);
  }, [loadLevelMembers]);

  // Reset team data
  const resetTeamData = useCallback(() => {
    setTeamData({
      levelBreakdown: [],
      totalTeamVolume: '0',
      totalLevelIncome: '0',
      totalTeamMembers: 0
    });
    setActivities([]);
    setLevelMembers({});
    setMembersPagination({});
  }, []);

  // Load data on mount
  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  // Utility functions
  const formatNumber = useCallback((num, decimals = 2) => {
    if (isNaN(num) || num === '' || num === null || num === undefined) return '0';
    return parseFloat(num).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }, []);

  const formatDate = useCallback((date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }, []);

  const formatAddress = useCallback((address) => {
    if (!address || address.length < 10) return address || '0x0000...0000';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="trail-team-dashboard">
        <div className="trail-loading-container">
          <div className="trail-ai-loading-spinner"></div>
          <p>Loading team data...</p>
          {hasTeamBreakdown && <small>Using optimized contract function</small>}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="trail-team-dashboard">
        <div className="trail-error-container">
          <div className="trail-error-icon">⚠️</div>
          <h3>Error Loading Team Data</h3>
          <p>{error}</p>
          <button className="trail-retry-btn" onClick={loadTeamData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="trail-team-dashboard">
      

      {/* Header */}
      <div className="trail-team-header">
        <div className="trail-team-title-section">
          <div className="trail-title-icon">👥</div>
          <div>
            <h1 className="trail-team-title">Team Overview</h1>
            <p className="trail-team-subtitle">Real-time team network analytics</p>
          </div>
        </div>
      </div>

      {/* Team Stats Overview */}
      <div className="trail-team-stats-grid">
        <div className="trail-team-stat-card trail-primary-stat">
          <div className="trail-stat-header">
            <div className="trail-stat-icon trail-volume-icon">📊</div>
            <div className="trail-stat-info">
              <div className="trail-stat-label">Total Team Volume</div>
              <div className="trail-stat-value">{formatNumber(teamData.totalTeamVolume)} USDT</div>
              
            </div>
          </div>
        </div>

        <div className="trail-team-stat-card trail-success-stat">
          <div className="trail-stat-header">
            <div className="trail-stat-icon trail-referrals-icon">👨‍👩‍👧‍👦</div>
            <div className="trail-stat-info">
              <div className="trail-stat-label">Total Team Members</div>
              <div className="trail-stat-value">{teamData.totalTeamMembers}</div>
              
            </div>
          </div>
        </div>

        <div className="trail-team-stat-card trail-info-stat">
          <div className="trail-stat-header">
            <div className="trail-stat-icon trail-direct-icon">🤝</div>
            <div className="trail-stat-info">
              <div className="trail-stat-label">Direct Referrals</div>
              <div className="trail-stat-value">{userInfo?.directReferrals || 0}</div>
            </div>
          </div>
        </div>

        <div className="trail-team-stat-card trail-warning-stat">
          <div className="trail-stat-header">
            <div className="trail-stat-icon trail-income-icon">💰</div>
            <div className="trail-stat-info">
              <div className="trail-stat-label">Level Income</div>
              <div className="trail-stat-value">{formatNumber(teamData.totalLevelIncome)} USDT</div>
            </div>
          </div>
        </div>
      </div>

      {/* Level Breakdown */}
      <div className="trail-team-content-grid">
        <div className="trail-team-content-card">
          <div className="trail-card-header">
            <h3>Level Breakdown ({teamData.totalTeamMembers} Total Members)</h3>
            <div className="trail-refresh-btn" onClick={loadTeamData}>
              🔄
            </div>
          </div>
          
          <div className="trail-level-breakdown-container">
            {teamData.levelBreakdown.map((level) => {
              const levelKey = `level_${level.level}`;
              const memberData = levelMembers[levelKey];
              const pagination = membersPagination[levelKey];
              const isLoading = loadingMembers[levelKey];
              const hasMembers = level.count > 0;
              
              return (
                <div 
                  key={level.level}
                  className={`trail-level-item ${expandedLevel === level.level ? 'expanded' : ''} ${hasMembers ? 'has-members' : ''}`}
                >
                  <div 
                    className="trail-level-summary"
                    onClick={() => handleLevelExpand(level.level)}
                    style={{ cursor: hasMembers ? 'pointer' : 'default' }}
                  >
                    <div className="trail-level-info">
                      <div className="trail-level-number">L{level.level}</div>
                      <div className="trail-level-details">
                        <div className="trail-level-count">{level.count} Members</div>
                        <div className="trail-level-volume">{formatNumber(level.volume)} USDT</div>
                      </div>
                    </div>
                    <div className="trail-level-commission">
                      {level.commission}
                    </div>
                    {hasMembers && (
                      <div className="trail-expand-arrow">
                        {expandedLevel === level.level ? '▼' : '▶'}
                      </div>
                    )}
                  </div>
                  
                  {expandedLevel === level.level && hasMembers && (
                    <div className="trail-level-expanded">
                      <div className="trail-level-stats">
                        <div className="trail-level-stat">
                          <span className="trail-stat-label">Active Members:</span>
                          <span className="trail-stat-value">{level.count}</span>
                        </div>
                        <div className="trail-level-stat">
                          <span className="trail-stat-label">Total Volume:</span>
                          <span className="trail-stat-value">{formatNumber(level.volume)} USDT</span>
                        </div>
                        <div className="trail-level-stat">
                          <span className="trail-stat-label">Commission Rate:</span>
                          <span className="trail-stat-value">{level.commission}</span>
                        </div>
                      </div>
                      
                      {/* Members List */}
                      <div className="trail-level-members">
                        <div className="trail-members-header">
                          <h4>Level {level.level} Members</h4>
                          {pagination && (
                            <div className="trail-pagination-info">
                              {pagination.totalMembers > 0 
                                ? `${pagination.totalMembers} total • Page ${pagination.page} of ${pagination.total}`
                                : 'No members'
                              }
                            </div>
                          )}
                        </div>
                        
                        {isLoading ? (
                          <div className="trail-members-loading">
                            <div className="trail-small-spinner"></div>
                            <span>Loading members...</span>
                          </div>
                        ) : memberData && memberData.members.length > 0 ? (
                          <>
                            <div className="trail-members-list">
                              {memberData.members.map((member) => (
                                <div key={member.id} className="trail-member-item">
                                  <div className="trail-member-info">
                                    <div className="trail-member-id">#{member.id}</div>
                                    <div className="trail-member-wallet">{formatAddress(member.wallet)}</div>
                                  </div>
                                  <div className="trail-member-stats">
                                    <div className="trail-member-staked">{formatNumber(member.totalStaked)} USDT</div>
                                    <div className="trail-member-volume">Vol: {formatNumber(member.teamVolume)} USDT</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Pagination Controls */}
                            {pagination && pagination.total > 1 && (
                              <div className="trail-pagination-controls">
                                <button 
                                  className="trail-page-btn"
                                  disabled={pagination.page <= 1}
                                  onClick={() => handlePageChange(level.level, pagination.page - 1)}
                                >
                                  Previous
                                </button>
                                <span className="trail-page-info">
                                  {pagination.page} / {pagination.total}
                                </span>
                                <button 
                                  className="trail-page-btn"
                                  disabled={pagination.page >= pagination.total}
                                  onClick={() => handlePageChange(level.level, pagination.page + 1)}
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </>
                        ) : memberData && memberData.error ? (
                          <div className="trail-error-message">
                            <span>Error loading members: {memberData.error}</span>
                          </div>
                        ) : (
                          <div className="trail-no-members">
                            <span>No member details available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Level Income History */}
        <div className="trail-team-content-card">
          <div className="trail-card-header">
            <h3>Level Income History</h3>
            <div className="trail-total-income">
              Total: {formatNumber(teamData.totalLevelIncome)} USDT
            </div>
          </div>
          
          <div className="trail-income-activities-list">
            {activities.length > 0 ? (
              activities.map((activity, index) => (
                <div key={`${activity.timestamp}-${index}`} className="trail-income-activity-item">
                  <div className="trail-activity-icon trail-level-reward-icon">🏆</div>
                  <div className="trail-activity-details">
                    <div className="trail-activity-name">Level {activity.level} Commission</div>
                    <div className="trail-activity-time">{formatDate(new Date(activity.timestamp * 1000))}</div>
                    <div className="trail-activity-from">From User #{activity.fromUserId}</div>
                  </div>
                  <div className="trail-activity-amount">+{formatNumber(activity.amount)} USDT</div>
                </div>
              ))
            ) : (
              <div className="trail-no-activities">
                <div className="trail-no-activities-icon">📈</div>
                <div className="trail-no-activities-text">No level income history yet</div>
                <div className="trail-no-activities-subtitle">
                  Start building your team to earn level commissions
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Building Guide */}
      <div className="trail-team-guide-card">
        <div className="trail-guide-header">
          <div className="trail-guide-icon">🎯</div>
          <div>
            <h3>Team Building Strategy</h3>
            <p>Maximize your earning potential through effective team building</p>
          </div>
        </div>
        
        <div className="trail-guide-content">
          <div className="trail-guide-section">
            <h4>Commission Structure</h4>
            <div className="trail-commission-grid">
              <div className="trail-commission-item">
                <span className="trail-commission-level">Direct (L1)</span>
                <span className="trail-commission-rate">6%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 2</span>
                <span className="trail-commission-rate">1%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 3</span>
                <span className="trail-commission-rate">3%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 4</span>
                <span className="trail-commission-rate">4%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 5</span>
                <span className="trail-commission-rate">6%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 6</span>
                <span className="trail-commission-rate">1%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 7-10</span>
                <span className="trail-commission-rate">0.75%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 11-15</span>
                <span className="trail-commission-rate">0.5%</span>
              </div>
              <div className="trail-commission-item">
                <span className="trail-commission-level">Level 16-20</span>
                <span className="trail-commission-rate">0.125%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamDetails;